import { CHANNEL_FORMATS, CAMPAIGN_STATUSES, CHANNEL_PRODUCT_TYPES, CHANNEL_OPTIONS, CHANNEL_PRODUCT_PURCHASES, CHANNEL_PRODUCT_YOUTUBE_DURATION } from '@/utils/campaignOptions'
import debounce from '@/utils/debounce'
import channelService from '@/api/channelService'
import formatters from '@/filters/customFormatters'
import authService, { USER_ROLES } from '@/api/authService'
import moment from 'moment'
import { mapActions } from 'vuex'
import { ACTIONS, MODULE_NAME } from '@/store/locations/constants'
import { CHANNEL_DEFAULT_KPIS } from '../../utils/campaignOptions'
import campaignsService from '@/api/campaignsService'
import i18n from '@/i18n'
import { isEqual } from 'lodash'

const newTemplate = () => ({ id: 'new', name: i18n.t('WITHOUT_TEMPLATE') })
const allOption = () => ({ id: 'all', label: i18n.t('ALL_ZIPCODES'), name: i18n.t('ALL_ZIPCODES'), code: i18n.t('ALL_ZIPCODES') })
const allOptionPOIs = () => ({ id: 'all', name: i18n.t('ALL_THE_POIS') })
const allOptionAdverts = () => ({ id: 'all', address: i18n.t('ALL_THE_ADVERTS') })
const DEBOUNCE_TIME = 500
const MULTIPRODUCT_LIMIT_MIN_HYBRID = 1
const MULTIPRODUCT_LIMIT_MIN_PRODUCT = 2
const MULTIPRODUCT_LIMIT_MAX = 8

const MIN_RADIUS = 2000
const INIT_RADIUS = 2500
const MAX_RADIUS = 5000

const POI_MIN_RADIUS = 100
const POI_INIT_RADIUS = 150
const POI_MAX_RADIUS = 1000

export default {
  name: 'ChannelSidebar',
  mixins: [formatters],
  components: {
    OptimizationChart: () => import('@/components/optimization-chart/OptimizationChart.vue'),
    CenterListItem: () => import('@/components/center-list-item/CenterListItem.vue'),
    StandListItem: () => import('@/components/stand-list-item/StandListItem.vue'),
    ZipcodeSelector: () => import(/* webpackChunkName: "ZipcodeSelector" */ '@/components/zipcode-selector/ZipcodeSelector.vue'),
    TagListSelector: () => import(/* webpackChunkName: "TagListSelector" */ '@/components/tag-list-selector/TagListSelector.vue'),
    CreativitiesUpload: () => import(/* webpackChunkName: "CreativitiesUpload" */'@/components/creativities-upload/CreativitiesUpload.vue')
  },
  props: {
    campaign: {
      type: Object,
      required: true
    },
    channel: {
      type: Object,
      required: true
    },
    adverts: {
      type: Object,
      required: true
    },
    pois: {
      type: Object,
      required: true
    },
    zones: {
      type: Array,
      required: true
    },
    zonesMeta: {
      type: Object,
      default: () => ({ loading: false, count: 0 })
    },
    boxSelectionEnabled: {
      type: Boolean,
      default: false
    },
    kpisDirty: {
      type: Boolean,
      default: false
    },
    templateUpdateError: {
      type: Object,
      required: false
    }
  },
  data () {
    return {
      kpisLoading: false,
      multiProductLimitMax: MULTIPRODUCT_LIMIT_MAX,
      authData: authService.authData,
      showFileError: false,
      ranges: {
        centers: {
          name: i18n.t('ACTION_RADIUS_ON_CENTERS'),
          shortName: 'Centros',
          value: INIT_RADIUS
        },
        pois: {
          name: i18n.t('ACTION_RADIUS_ON_POIS'),
          shortName: 'POIs',
          value: POI_INIT_RADIUS
        },
        adverts: {
          name: i18n.t('ACTION_RADIUS_ON_ADVERTS'),
          shortName: 'Soportes',
          value: INIT_RADIUS
        }
      },
      selectedAreaTab: null,
      templateOptions: [],
      productOptions: [],
      targetOptions: [],
      productTypeOptions: Object.values(CHANNEL_PRODUCT_TYPES).filter((pType) => {
        return !(this.$route.params.channeltype === CHANNEL_OPTIONS.GOOGLE && pType.value === CHANNEL_PRODUCT_TYPES.HYBRID.value)
      }),
      productPurchaseOptions: Object.values(CHANNEL_PRODUCT_PURCHASES),
      productYoutubeDurationOptions: CHANNEL_PRODUCT_YOUTUBE_DURATION,
      formatOptions: Object.entries(CHANNEL_FORMATS).filter(([_, value]) => {
        return value.channels.includes(this.$route.params.channeltype)
      }).map(([key, value]) => ({ id: key, name: value.name })),
      chartDropdownExpanded: false,
      templateFormVisible: false,
      areasHelp: {
        title: i18n.t('AREAS_HELP_TITLE'),
        text: i18n.t('AREAS_HELP_BODY')
      },
      formatsHelp: {
        title: i18n.t('FORMAT_HELP_TITLE'),
        text: i18n.t('FORMAT_HELP_TEXT')
      },
      kpisInterval: null,
      ctas: [],
      errors: [],
      initialChannel: null
    }
  },
  watch: {
    'channel.template': {
      handler (template, oldTemplate) {
        if (template && oldTemplate && template.id === oldTemplate.id) {
          return
        }
        this.templateFormVisible = !!template
        const products = (template && template.products) || []
        const target = (template && template.target) || null
        this.$set(this.channel, 'products', products)
        this.$set(this.channel, 'target', target)
      }
    },
    'channel.products': {
      handler (products) {
        if (this.type === CHANNEL_OPTIONS.GEOLOCATION) {
          const categories = products.map(p => Number(p.category))
          const productPois = this.getPoisForCategories(categories)
          // const productPoisIds = productPois.map(p => p.id)
          // const otherPois = this.channel.pois.filter(p => productPoisIds.indexOf(p.id) === -1)
          this.channel.pois = productPois // [...otherPois, ...productPois]
        }
      }
    },
    channel: {
      deep: true,
      handler (value) {
        // Compare the initial channel with the current one
        // to check if the channel has been modified
        if (this.initialChannel) {
          const isModified = !isEqual(this.initialChannel, value)
          this.$emit('channelChange', isModified)
        }
      }
    },
    // Form validation
    'channel.creativity_url': {
      handler (value) {
        // binding this to the data value in the opening_creativity_url input
        this.validateUrl(value, 'creativity_url')
      }
    },
    'channel.opening_creativity_url': {
      handler (value) {
        this.validateUrl(value, 'opening_creativity_url')
      }
    },
    'channel.closing_creativity_url': {
      handler (value) {
        this.validateUrl(value, 'closing_creativity_url')
      }
    },
    'channel.video_url': {
      handler (value) {
        this.validateUrl(value, 'video_url')
      }
    }
  },
  computed: {
    campaignType () {
      return CHANNEL_PRODUCT_TYPES[this.channel.direction].label
    },
    type () {
      return this.$route.params.channeltype
    },
    startDate () {
      return this.channel.start_date || this.campaign.start_date
    },
    finishDate () {
      return this.channel.finish_date || this.campaign.finish_date
    },
    selectableDates () {
      return [
        this.campaign.start_date,
        this.campaign.finish_date
      ]
    },
    templateIsNew () {
      return this.channel.template && this.channel.template.id === 'new'
    },
    targetTags () {
      if (!this.channel.target) {
        return []
      }
      const { gender_ages: population, interests } = this.channel.target
      const populationTags = population.map(el => (
        `${el.gender} ${el.age_range}`
      )).sort()
      const interestTags = interests.map(el => el.value).sort()
      return [...populationTags, ...interestTags]
    },
    isDirector () {
      return this.authData.user.role === USER_ROLES.DIRECTOR
    },
    readonly () {
      const { CENTRAL_ACCEPTED, CENTRAL_REJECTED, AGENCY_CONFIRMED, FINISHED, ACTIVE } = CAMPAIGN_STATUSES
      return this.channel.id && [CENTRAL_ACCEPTED, CENTRAL_REJECTED, AGENCY_CONFIRMED, FINISHED, ACTIVE].indexOf(this.campaign.status) !== -1
    },
    readonlyAreas () {
      if (this.readonly) {
        return true
      }
      return (this.isInterest || this.isYoutube) && (!this.channel.id || this.selectedAreaTab === 'zone')
    },
    year () {
      return new Date().getFullYear()
    },
    budget () {
      return Number(this.channel.budget || 0)
    },
    budgetPercent () {
      return ((this.channel.budget / this.campaign.budget) * 100) || 0
    },
    duration () {
      const start = moment(this.channel.start_date)
      const finish = moment(this.channel.finish_date)
      return finish.diff(start, 'days')
    },
    selectedRange () {
      if (this.selectedAreaTab === 'zone') {
        return { name: i18n.t('ZONES') }
      }
      const key = `${this.selectedAreaTab}s`
      return this.ranges[key] || {}
    },
    areas () {
      if (this.selectedAreaTab === 'zone') {
        if (this.isInterest || this.isYoutube) {
          return [allOption()]
        }
        return this.channel.zones
      }
      if (this.selectedAreaTab === 'center') {
        return this.channel.shops
      }
      if (this.selectedAreaTab === 'poi') {
        return this.channel.pois
      }
      if (this.selectedAreaTab === 'advert') {
        return this.channel.adverts
      }
      return []
    },
    rangeMax () {
      return this.selectedAreaTab === 'poi' ? POI_MAX_RADIUS : MAX_RADIUS
    },
    rangeMin () {
      return this.selectedAreaTab === 'poi' ? POI_MIN_RADIUS : MIN_RADIUS
    },
    isProduct () {
      return this.channel.direction === CHANNEL_PRODUCT_TYPES.REDIRECT.value
    },
    isService () {
      return this.channel.direction === CHANNEL_PRODUCT_TYPES.DIRECT.value
    },
    isHybrid () {
      return this.channel.direction === CHANNEL_PRODUCT_TYPES.HYBRID.value
    },
    multiProductLimitMin () {
      return this.isHybrid ? MULTIPRODUCT_LIMIT_MIN_HYBRID : MULTIPRODUCT_LIMIT_MIN_PRODUCT
    },
    productTypeLabelPluralized () {
      return this.isService
        ? this.hasMultipleProducts
          ? i18n.t('SERVICES')
          : i18n.t('SERVICE')
        : this.hasMultipleProducts
          ? i18n.t('PRODUCTS')
          : i18n.t('PRODUCT')
    },
    productTypeLabel () {
      return this.isService
        ? CHANNEL_PRODUCT_TYPES.DIRECT.label
        : CHANNEL_PRODUCT_TYPES.REDIRECT.label
    },
    eficiencyKPIs () {
      const defaultKpis = [
        { total: 0, key: 'impacts', label: i18n.t('ESTIMATED_IMPRESSIONS'), value: this.channel.impacts || null },
        { total: 0, key: 'coverage', label: i18n.t('ESTIMATED_COVERAGE'), value: this.channel.coverage || null }
        // { label: 'Optimización', value: this.channel.optimization }
      ]
      return this.channel.kpis
        ? this.channel.kpis
        : CHANNEL_DEFAULT_KPIS[this.channel.type]
          ? defaultKpis.filter(kpi => CHANNEL_DEFAULT_KPIS[this.channel.type].indexOf(kpi.key) !== -1)
          : defaultKpis
    },
    hasMultipleProducts () {
      if (this.isService) {
        return false
      }
      if (this.isHybrid) {
        return true
      }
      return this.channel.format && (
        this.channel.format.id === CHANNEL_FORMATS.FACEBOOK_CARROUSEL.key ||
        this.channel.format.id === CHANNEL_FORMATS.MOBILE_CARROUSEL.key
      )
    },
    zonesOptions () {
      return [allOption()].concat(this.zones)
    },
    poisOptions () {
      return [allOptionPOIs()].concat(this.pois.data)
    },
    advertOptions () {
      return [allOptionAdverts()].concat(this.adverts.data)
    },
    isInterest () {
      return this.type === CHANNEL_OPTIONS.INTEREST
    },
    isGoogle () {
      return this.type === CHANNEL_OPTIONS.GOOGLE
    },
    isFacebook () {
      return this.type === CHANNEL_OPTIONS.FACEBOOK
    },
    isInstagram () {
      return this.type === CHANNEL_OPTIONS.INSTAGRAM
    },
    isYoutube () {
      return this.type === CHANNEL_OPTIONS.YOUTUBE
    },
    isGeolocation () {
      return this.type === CHANNEL_OPTIONS.GEOLOCATION
    },
    // not used anywhere
    isLocalInventoryAds () {
      return this.type === CHANNEL_OPTIONS.LOCALINVENTORYADS
    },
    isGoogleSearchAds () {
      return this.type === CHANNEL_OPTIONS.GOOGLESEARCHADS
    },
    isMeta () {
      return this.type === CHANNEL_OPTIONS.META
    },
    isRetargeting () {
      return this.type === CHANNEL_OPTIONS.RETARGETING
    },
    productUrl () {
      return this.channel.products[0] && this.channel.products[0].url
    },
    productsLengthOk () {
      const len = this.channel.products.length
      return len >= this.multiProductLimitMin && len <= this.multiProductLimitMax
    },
    dateRangeOk () {
      const minTime = new Date(this.campaign.start_date).getTime()
      const maxTime = new Date(this.campaign.finish_date).getTime()
      const startTime = moment(this.startDate).endOf('day').toDate().getTime()
      const endTime = moment(this.finishDate).startOf('day').toDate().getTime()
      return startTime >= minTime && endTime <= maxTime
    },
    notValidBudget () {
      if (!this.channel.budget) return false

      const days = moment(this.finishDate).diff(moment(this.startDate), 'days')
      const costDay = [CHANNEL_OPTIONS.INTEREST, CHANNEL_OPTIONS.RETARGETING, CHANNEL_OPTIONS.FACEBOOK, CHANNEL_OPTIONS.INSTAGRAM].includes(this.type) ? 5 : this.type === CHANNEL_OPTIONS.YOUTUBE ? 50 : 7

      return ((days * costDay) > this.channel.budget) ? costDay : false
    }
  },
  created () {
    this.searchCTAs = debounce(this._searchCTAs, DEBOUNCE_TIME)
  },
  mounted () {
    this.templateFormVisible = !!this.channel.template
    this._templateSearch = debounce(this.templateSearch, DEBOUNCE_TIME)
    this._productSearch = debounce(this.productSearch, DEBOUNCE_TIME + 250)
    this._targetSearch = debounce(this.targetSearch, DEBOUNCE_TIME)
    this._zipcodeSearch = debounce(this.zipcodeSearch, DEBOUNCE_TIME)
    this._poiSearch = debounce(this.poiSearch, DEBOUNCE_TIME)
    this._advertSearch = debounce(this.advertSearch, DEBOUNCE_TIME)

    // GEOLOCATION, needs to be set after the channel is loaded
    if (this.type === CHANNEL_OPTIONS.GEOLOCATION) {
      this.$set(this.channel, 'direction', CHANNEL_PRODUCT_TYPES.DIRECT.value)
    }

    this.templateSearch('')
    this.productSearch('')
    this.targetSearch('')
    this.searchCTAs('')

    this.setupAreaRanges()
    this.selectAreaTab({ id: this.getInitialAreaTab() })
    this.setCreativitiesURL()
    this.setStartFrameURL()
    this.setEndFrameURL()

    if (this.isYoutube) {
      this.setYoutubeOptions()
    }

    this.$set(this.channel, 'creativity_url', this.channel.creativity_url)

    setTimeout(() => { this.initialChannel = { ...this.channel } }, 1000)
  },

  beforeDestroy () {
    clearTimeout(this.kpisInterval)
    this.kpisInterval = null
  },
  methods: {
    ...mapActions(MODULE_NAME, Object.values(ACTIONS)),
    getPoisForCategories (categories) {
      return this.pois.data.filter(poi => this.getPoiCategories(poi).some(cat => categories.indexOf(cat) !== -1))
    },
    getPoiCategories (poi) {
      let categories = []
      if (poi.categories) {
        categories = poi.categories.map(c => c.number)
      }

      return categories && categories.length ? categories : [Number(poi.category)]
    },
    setupAreaRanges () {
      if (this.channel.shops.length > 0) {
        this.ranges.centers.value = Number(this.channel.shops[0].radius)
      }
      if (this.channel.pois.length > 0) {
        this.ranges.pois.value = Number(this.channel.pois[0].radius)
      }
      if (this.channel.adverts.length > 0) {
        this.ranges.adverts.value = Number(this.channel.adverts[0].radius)
      }
    },
    getInitialAreaTab () {
      if (!this.channel.id) {
        return this.canShowTab('zone') ? 'zone' : 'center'
      }
      if (this.channel.zones && this.channel.zones.length > 0) {
        return 'zone'
      }
      if (this.channel.shops && this.channel.shops.length > 0) {
        return 'center'
      }
      if (this.channel.pois && this.channel.pois.length > 0) {
        return 'poi'
      }
      if (this.channel.adverts && this.channel.adverts.length > 0) {
        return 'advert'
      }
    },
    selectAreaTab (tab) {
      this.selectedAreaTab = tab.id
      this.emitAreaChange(tab.id)
    },
    emitAreaChange (entity) {
      const range = this.ranges[`${entity}s`]
      this.$emit('areaChange', { entity, radius: range && range.value })
    },
    areaTabIsSelected (id) {
      return id === this.selectedAreaTab
    },
    productIsSelected (product) {
      return this.channel.products.some(p => p.id === product.id)
    },
    slowTemplateSearch (query) { this._templateSearch(query) },
    slowProductSearch (query) { this._productSearch(query) },
    slowTargetSearch (query) { this._targetSearch(query) },
    slowZipcodeSearch (query) { this._zipcodeSearch(query) },
    slowPoiSearch (query) { this._poiSearch(query) },
    slowAdvertSearch (query) { this._advertSearch(query) },
    _searchCTAs (query) {
      campaignsService.getCTAs({ q: query }).then(data => {
        this.ctas = data
      })
    },
    templateSearch (query) {
      let params = {}
      if (this.channel.direction) {
        if (this.channel.direction !== CHANNEL_PRODUCT_TYPES.DIRECT.value && this.type !== CHANNEL_OPTIONS.GEOLOCATION) {
          params = { 'products__is_service': false }
        } else {
          params = { 'products__is_service': true }
        }
      }
      channelService.getSummary('templates', query, params).then(data => {
        this.templateOptions = [newTemplate()].concat(data)
      })
    },
    productSearch (query) {
      const shopids = this.campaign.shops.map(s => s.id)
      channelService.getSummary('products', query, {
        is_service: this.isService,
        shop_related__in: shopids,
        date: moment(this.campaign.created_at).format('YYYY-MM-DD')
      }).then(data => {
        this.productOptions = data.sort((a, b) => a.shop_related.id - b.shop_related.id)
      })
    },
    targetSearch (query) {
      channelService.getSummary('targets', query).then(data => {
        this.targetOptions = data
      })
    },
    zipcodeSearch (query) {
      const shopids = this.campaign.shops.map(s => s.id)
      this[ACTIONS.FETCH_ZONES]({ reset: true, q: query, in_shop_influence_area: shopids, query: 1 })
    },
    poiSearch (query) {
      const shopids = this.campaign.shops.map(s => s.id)
      this[ACTIONS.FETCH_POIS]({ page_size: 10000, reset: true, q: query, in_shop_influence_area: shopids, query: 1, ordering: 'name' })
    },
    advertSearch (query) {
      const shopids = this.campaign.shops.map(s => s.id)
      this[ACTIONS.FETCH_ADVERTS]({ reset: true, q: query, shop_related__in: shopids, query: 1, ordering: 'advert_id,address', date: this.campaign.created_at })
    },
    onChartDropdownToggle () {
      this.chartDropdownExpanded = !this.chartDropdownExpanded
      this.updateScrollbar()
    },
    onDatepickerChange (dates) {
      const [start, finish] = dates
      this.channel.start_date = start
      this.channel.finish_date = finish
    },
    onSelectFormat (value) {
      this.$refs.formatSelector.close()
      this.$set(this.channel, 'format', value)
    },
    onSelectTemplate (value) {
      this.$refs.templateSelector.close()
      this.$set(this.channel, 'template', value)
    },
    onSelectTarget (value) {
      this.$refs.targetSelector.close()
      this.$set(this.channel, 'target', value)
    },
    onSelectCTA (value) {
      this.$refs.ctaSelector.close()
      this.$set(this.channel, 'cta', value)
    },
    onProductSelected (product) {
      if (this.hasMultipleProducts) {
        if (this.productIsSelected(product)) {
          this.channel.products = this.channel.products.filter(p => p.id !== product.id)
        } else {
          this.channel.products = this.channel.products.concat(product)
          if (this.channel.products.length >= MULTIPRODUCT_LIMIT_MAX) {
            this.$refs.productSelector.close()
          }
        }
      } else {
        this.channel.products = [product]
        this.$refs.productSelector.close()
      }
    },
    onDirectionChange () {
      this.productSearch('')
      this.$set(this.channel, 'products', [])
      this.templateSearch('')
      this.$set(this.channel, 'template', null)
    },
    onPurchaseChange (purchase) {
      this.productSearch('')
      if (purchase === CHANNEL_PRODUCT_PURCHASES.CPV.value) {
        this.channel.video_length = this.productYoutubeDurationOptions[2].value
        this.productYoutubeDurationOptions = CHANNEL_PRODUCT_YOUTUBE_DURATION.filter(o => o.value === '>20')
      } else {
        this.productYoutubeDurationOptions = CHANNEL_PRODUCT_YOUTUBE_DURATION
      }
      this.$set(this.channel, 'purchase', purchase)
    },
    onYoutubeDurationChange (duration) {
      this.productSearch('')
      this.$set(this.channel, 'video_length', duration)
    },
    updateScrollbar () {
      window.setTimeout(() => {
        if (this.$refs.scrollbar) this.$refs.scrollbar.update()
      }, 250)
    },
    onCenterRadiusChange (value) {
      this.ranges.centers.value = Number(value)
      this.emitAreaChange('center')
    },
    onPOIRadiusChange (value) {
      this.ranges.pois.value = Number(value)
      this.emitAreaChange('poi')
    },
    onAdvertRadiusChange (value) {
      this.ranges.adverts.value = Number(value)
      this.emitAreaChange('advert')
    },
    onToggleSaveAsTemplate (state) {
      this.$set(this.channel, 'saveAsTemplate', state)
    },
    getProductSubtext (product) {
      return `${i18n.t('REFERENCE_SHORT')} ${product.reference} - ${product.shop_related.name}`
    },
    async updateKPIs () {
      if (!this.channel.budget) {
        this.channel.impacts = 0
        this.channel.coverage = 0
      }
    },
    toggleBoxSelection ({ state }) {
      this.$emit('toggleBoxSelection', state)
    },
    setCreativitiesURL () {
      let url = null
      if (this.channel.direction === CHANNEL_PRODUCT_TYPES.DIRECT.value && this.channel.ext_creativities) {
        url = this.channel.ext_creativities[0].url
      }
      this.channel.creativity_url = url
    },
    setStartFrameURL () {
      let url = null
      if (this.channel.direction === CHANNEL_PRODUCT_TYPES.HYBRID.value && this.channel.ext_creativities) {
        const startCreativities = this.channel.ext_creativities.filter(c => c.frame === 'START')
        if (startCreativities) url = startCreativities[0].url
      }
      this.channel.opening_creativity_url = url
    },
    setEndFrameURL () {
      let url = null
      if (this.channel.direction === CHANNEL_PRODUCT_TYPES.HYBRID.value && this.channel.ext_creativities) {
        const endCreativities = this.channel.ext_creativities.filter(c => c.frame === 'END')

        if (endCreativities.length > 0) url = endCreativities[0].url
      }
      this.channel.closing_creativity_url = url
    },
    setYoutubeOptions () {
      this.channel.purchase_type = this.channel.purchase_type || CHANNEL_PRODUCT_PURCHASES['CPM'].value
      this.channel.video_length = this.channel.video_length || this.productYoutubeDurationOptions[0].value
    },
    canShowTab (tab) {
      // tab can be 'zone', 'center', 'poi' or 'advert'
      if (this.type === CHANNEL_OPTIONS.RETARGETING || this.isYoutube) {
        return tab === 'zone'
      }
      if (this.type === CHANNEL_OPTIONS.FACEBOOK || this.type === CHANNEL_OPTIONS.INSTAGRAM) {
        return tab === 'zone' || tab === 'center'
      }

      return true
    },
    toggleEditProduct (product) {
      this.$set(product, 'url_overriden', !product.url_overriden)
    },
    isValidHttpUrl (string) {
      let url

      try {
        url = new URL(string)
      } catch (_) {
        return false
      }

      return url.protocol === 'https:'
    },
    getGroupSelectorParams (type) {
      return {
        type,
        shops: this.campaign.shops.map(s => s.id)
      }
    },
    onGeoTemplateSelected (id) {
      this.$set(this.channel, 'geo_template_id', id)
    },
    validateUrl (value, field) {
      if (value && value.length < 3) return

      // eslint-disable-next-line no-useless-escape
      const regex = /^(?:\w+:)?\/\/([^\s\.]+\.\S{2}|localhost[\:?\d]*)\S*$/
      if (regex.test(value)) {
        this.errors[field] = ''
      } else {
        this.errors[field] = i18n.t('INVALIDA_URL_FORMAT')
      }
    }
  },
  filters: {
    date (value) {
      const date = value instanceof Date ? value : new Date(value)
      return date.toLocaleDateString()
    }
  }
}
